import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import BlendedOfferingsService from "../Service/BlendedOfferingsService";
import BlendedNominationService from "../Service/BlendedNominationService";
import BlendedService from "../Service/BlendedService";
import { useFreezeNomination } from "../Context/FreezeNominationContext";

function SMEBlendedLearningNomination() {
  const location = useLocation();
  const { user } = location.state || {};
  const [selectedMonth, setSelectedMonth] = useState("");
  const [expandedPrograms, setExpandedPrograms] = useState({});
  const [existingOfferings, setExistingOfferings] = useState([]);
  const [loadingExistingOfferings, setLoadingExistingOfferings] = useState(false);
  const { isFrozen } = useFreezeNomination();

  const [nominations, setNominations] = useState({});
  const [nominatedPrograms, setNominatedPrograms] = useState(new Set());
  const [existingNominations, setExistingNominations] = useState(new Set());

  // Helper function to check nomination status for a program
  const getNominationStatus = (program) => {
    let newNominations = 0;
    
    program.modules.forEach((module, moduleIndex) => {
      module.connects.forEach((connect, connectIndex) => {
        const key = `${program.id}-${moduleIndex}-${connectIndex}`;
        // Check if user is already nominated for this connect
        const userAlreadyNominated = connect.nominations && 
          connect.nominations.some(nom => nom.smeEmail === user.email);
        // Count new nominations (checkbox selected but not already nominated)
        if (nominations[key] === true && !userAlreadyNominated) {
          newNominations++;
        }
      });
    });
    
    return { newNominations };
  };

  // checkbox handler
  const handleNominationCheckbox = (programId, moduleIndex, connectIndex, checked) => {
    const key = `${programId}-${moduleIndex}-${connectIndex}`;

    setNominations(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  // submit nomination handler
  const handleSubmitNomination = async (program) => {
    try {
      const payload = {
        blendedProgramOfferingId: program.blendedProgramId,
        month: selectedMonth,
        blendedProgramName: program.blendedProgramName,
        programWDCourseCode: program.programWDCourseCode,
        track: program.track,
        modules: program.modules.map((module, moduleIndex) => ({
          ...(module.moduleId && { moduleId: module.moduleId }), // Include moduleId only if it exists
          id: module.id,
          moduleName: module.moduleName,
          moduleWDCourseCode: module.moduleWDCourseCode,
          description: module.description,
          duration: module.duration,
          numOfSMEConnects: module.numOfSMEConnects,
          connects: module.connects
            .map((connect, connectIndex) => {
              const key = `${program.id}-${moduleIndex}-${connectIndex}`;
              const isNominated = nominations[key] === true;
              
              if (!isNominated) return null;
              
              // Check if user is already nominated for this connect
              const userAlreadyNominated = connect.nominations && 
                connect.nominations.some(nom => nom.smeEmail === user.email);
              
              if (userAlreadyNominated) {
                // If already nominated, don't add another nomination, just return the connect
                return {
                  connectId: connect.connectId,
                  date: connect.date,
                  time: connect.time,
                  day: connect.day,
                  nominations: connect.nominations // Keep existing nominations as is
                };
              }
              
              // Only add new nomination if not already nominated
              return {
                connectId: connect.connectId,
                date: connect.date,
                time: connect.time,
                day: connect.day,
                nominations: [
                  ...(connect.nominations || []),
                  {
                    smeEmail: user.email,
                    status: "NOMINATED",
                    nominationTime: new Date().toISOString()
                  }
                ]
              };
            })
            .filter(connect => connect !== null),
          assessmentDate: module.assessmentDate,
          quizDate: module.quizDate
        }))
          .filter(module => module.connects.length > 0),
        status: "NOMINATED",
        publishedDate: program.publishedDate,
        publishedBy: program.publishedBy,
        openForNomination: program.openForNomination
      };

      console.log("Nomination Payload:", payload);
      if (!payload.blendedProgramOfferingId) {
        alert("Program ID not found. Unable to nominate.");
        return;
      }

      if (payload.modules.length === 0) {
        alert("Please select at least one date for nomination.");
        return;
      }

      await BlendedNominationService.submitSMENomination(payload);

      // Update the existing nominations tracking
      setExistingNominations(prev => new Set([...prev, program.id]));
      setNominatedPrograms(prev => new Set([...prev, program.id]));

      // Refresh the table data to show updated nominations
      await refreshOfferings();

      // Clear the nominations state for this program to reset checkboxes
      setNominations(prev => {
        const updated = { ...prev };
        program.modules.forEach((module, moduleIndex) => {
          module.connects.forEach((connect, connectIndex) => {
            const key = `${program.id}-${moduleIndex}-${connectIndex}`;
            delete updated[key];
          });
        });
        return updated;
      });

      alert(`SME nomination ${existingNominations.has(program.id) ? 'updated' : 'submitted'} successfully!`);
    } catch (error) {
      console.error("Nomination error:", error);
      alert("Failed to submit nomination.");
    } finally {
      // setLoading(false);
    }
  };

  // drop nomination handler
  const handleDropNomination = async (program) => {
    try {
      const confirmDrop = window.confirm(
        `Are you sure you want to drop your nomination for "${program.blendedProgramName}"?`
      );
      
      if (!confirmDrop) {
        return;
      }

      // Call backend API to drop nomination
      await BlendedNominationService.dropSMENomination({
        blendedProgramOfferingId: program.blendedProgramId,
        smeEmail: user.email,
        month: selectedMonth
      });

      // Remove from nominated and existing nominations tracking
      setNominatedPrograms(prev => {
        const updated = new Set(prev);
        updated.delete(program.id);
        return updated;
      });

      setExistingNominations(prev => {
        const updated = new Set(prev);
        updated.delete(program.id);
        return updated;
      });

      // Clear nominations for this program
      setNominations(prev => {
        const updated = { ...prev };
        program.modules.forEach((module, moduleIndex) => {
          module.connects.forEach((connect, connectIndex) => {
            const key = `${program.id}-${moduleIndex}-${connectIndex}`;
            delete updated[key];
          });
        });
        return updated;
      });

      alert("Nomination dropped successfully!");
      
      // Refresh the offerings to get updated data
      window.location.reload(); // Temporary solution - ideally should refetch data
    } catch (error) {
      console.error("Drop nomination error:", error);
      alert("Failed to drop nomination: " + (error.message || "Unknown error"));
    }
  };

  // drop single connect nomination handler
  const handleDropSingleConnect = async (program, moduleIndex, connectIndex) => {
    try {
      const module = program.modules[moduleIndex];
      const connect = module.connects[connectIndex];
      
      const confirmDrop = window.confirm(
        `Are you sure you want to drop your nomination for "${module.moduleName}" on ${connect.date} at ${connect.time}?`
      );
      
      if (!confirmDrop) {
        return;
      }

      // Call backend API to drop single connect nomination
      await BlendedNominationService.dropSingleSMEConnect({
        connectId: connect.connectId,
        smeEmail: user.email
      });

      // Remove this specific connect from nominations
      const key = `${program.id}-${moduleIndex}-${connectIndex}`;
      setNominations(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });

      // Update the connect's nominations to remove the user's nomination
      const updatedOfferings = existingOfferings.map(offering => {
        if (offering.id === program.id) {
          const updatedModules = offering.modules.map((mod, mIdx) => {
            if (mIdx === moduleIndex) {
              const updatedConnects = mod.connects.map((conn, cIdx) => {
                if (cIdx === connectIndex) {
                  return { 
                    ...conn, 
                    nominations: conn.nominations.filter(nom => nom.smeEmail !== user.email)
                  };
                }
                return conn;
              });
              return { ...mod, connects: updatedConnects };
            }
            return mod;
          });
          return { ...offering, modules: updatedModules };
        }
        return offering;
      });
      setExistingOfferings(updatedOfferings);

      // Check if this was the last nomination for this program
      const hasRemainingNominations = Object.keys(nominations).some(k => 
        k.startsWith(program.id) && k !== key && nominations[k] === true
      );
      
      if (!hasRemainingNominations) {
        setExistingNominations(prev => {
          const updated = new Set(prev);
          updated.delete(program.id);
          return updated;
        });
        setNominatedPrograms(prev => {
          const updated = new Set(prev);
          updated.delete(program.id);
          return updated;
        });
      }

      alert(`Nomination for ${module.moduleName} on ${connect.date} dropped successfully!`);
    } catch (error) {
      console.error("Drop single connect nomination error:", error);
      alert("Failed to drop connect nomination: " + (error.message || "Unknown error"));
    }
  };

  // Function to refresh offerings data
  const refreshOfferings = async () => {
    if (!selectedMonth || !user?.track) {
      setExistingOfferings([]);
      return;
    }
    try {
      setLoadingExistingOfferings(true);
      // Get only programs that are open for nomination and belong to user's track
      const blendedOfferings = await BlendedNominationService.getBlendedProgramsOpenForNominationByMonthAndTrack(selectedMonth, user.track);
      const transformedExistingOfferings = blendedOfferings
        .filter(offering => offering.blendedProgramName && offering.openForNomination)
        .filter(program => {
          // Filter out programs where user is already nominated for ALL connects
          if (!program.modules || program.modules.length === 0) return true;
          
          let totalConnects = 0;
          let userNominatedConnects = 0;
          
          program.modules.forEach(module => {
            if (module.connects && module.connects.length > 0) {
              module.connects.forEach(connect => {
                totalConnects++;
                if (connect.nominations && connect.nominations.some(nom => nom.smeEmail === user.email)) {
                  userNominatedConnects++;
                }
              });
            }
          });
          
          // Show program only if user is not nominated for all connects
          return totalConnects === 0 || userNominatedConnects < totalConnects;
        })
        .map((program, index) => ({
          id: `existing-${program.blendedProgramOfferingId || index}`,
          blendedProgramName: program.blendedProgramName,
          blendedProgramId: program.blendedProgramOfferingId,
          programWDCourseCode: program.programWDCourseCode,
          track: program.track,
          publishedDate: program.publishedDate,
          publishedBy: program.publishedBy,
          status: program.status,
          openForNomination: program.openForNomination,
          modules: program.modules ? program.modules.map((module) => ({
            ...module,
            moduleId: module.moduleId || "", // Preserve backend moduleId
            connects: module.connects ? module.connects.map((connect) => ({
              connectId: connect.connectId,
              date: connect.date || "",
              time: connect.time || "",
              day: connect.day || "",
              nominations: connect.nominations || [], // Use nominations array
              collectiveStatus: connect.collectiveStatus,
              finalizedSME: connect.finalizedSME
            })) : Array.from({ length: module.numOfSMEConnects || 1 }, () => ({
              connectId: `temp-${Date.now()}-${Math.random()}`,
              date: "",
              time: "",
              day: "",
              nominations: [],
              collectiveStatus: "NOT YET CONFIRMED",
              finalizedSME: null
            })),
            assessmentDate: module.assessmentDate || "",
            quizDate: module.quizDate || "",
          })) : [],
          isEditing: false,
          isExisting: true
        }));
      setExistingOfferings(transformedExistingOfferings);
    } catch (error) {
      console.error("Error fetching existing offerings:", error);
      setExistingOfferings([]);
    } finally {
      setLoadingExistingOfferings(false);
    }
  };

  // Fetch programs open for nomination for user's track when selectedMonth changes
  useEffect(() => {
    refreshOfferings();
  }, [selectedMonth, user?.track]);

  // Initialize existing nominations when programs are loaded
  useEffect(() => {
    if (existingOfferings.length > 0 && user?.email) {
      const existingNoms = new Set();
      const preSelectedNominations = {};
      
      existingOfferings.forEach((program) => {
        let hasNomination = false;
        program.modules.forEach((module, moduleIndex) => {
          module.connects.forEach((connect, connectIndex) => {
            // Check if user is in nominations array for this connect
            if (connect.nominations && connect.nominations.some(nom => nom.smeEmail === user.email)) {
              const key = `${program.id}-${moduleIndex}-${connectIndex}`;
              preSelectedNominations[key] = true;
              hasNomination = true;
            }
          });
        });
        if (hasNomination) {
          existingNoms.add(program.id);
        }
      });
      
      setExistingNominations(existingNoms);
      setNominatedPrograms(existingNoms);
      setNominations(preSelectedNominations);
    }
  }, [existingOfferings, user?.email]);

  const toggleProgramExpansion = (programId) => {
    setExpandedPrograms(prev => ({
      ...prev,
      [programId]: !prev[programId]
    }));
  };

  // Render program table rows (simplified)
  const renderProgramTable = (program) => (
    <div className="px-6 pb-6">
      <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[1300px] table-fixed">
            <thead>
              <tr className="bg-gray-200 text-gray-600">
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-48">Module</th>
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-64">Description</th>
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-24">Duration</th>
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-40">Date</th>
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-48">Time</th>
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-32">Day</th>
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-32">Nominate</th>
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-40">Assessment</th>
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-40">Quiz</th>
              </tr>
            </thead>
            <tbody>
              {program.modules.map((mod, moduleIndex) =>
                mod.connects.map((connect, connectIndex) => (
                  <tr
                    key={`${moduleIndex}-${connectIndex}`}
                    className="hover:bg-gray-50 border-b border-gray-200"
                  >
                    {connectIndex === 0 && (
                      <>
                        <td rowSpan={mod.connects.length} className="px-4 py-3 border-r border-gray-200 text-sm font-medium align-top">
                          <div className="mb-2">{mod.moduleName}</div>

                        </td>
                        <td rowSpan={mod.connects.length} className="px-4 py-3 border-r border-gray-200 text-sm align-top">
                          {mod.description}
                        </td>
                        <td rowSpan={mod.connects.length} className="px-4 py-3 border-r border-gray-200 text-sm text-center font-semibold text-gray-600 align-top">
                          {mod.duration}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3 border-r border-gray-200 text-sm">
                      {connect.date}

                    </td>
                    <td className="px-4 py-3 border-r border-gray-200 text-sm">
                      {connect.time}

                    </td>
                    <td className="px-4 py-3 border-r border-gray-200 text-sm">
                      {connect.day}
                    </td>
                    <td className="px-4 py-3 border-r border-gray-200 text-sm text-center">
                      <div className="flex flex-col items-center space-y-1">
                        {(() => {
                          const userNominated = connect.nominations && 
                            connect.nominations.some(nom => nom.smeEmail === user.email);
                          return (
                            <>
                              <input
                                type="checkbox"
                                disabled={userNominated || isFrozen}
                                checked={nominations[`${program.id}-${moduleIndex}-${connectIndex}`] || userNominated}
                                onChange={(e) =>
                                  handleNominationCheckbox(
                                    program.id,
                                    moduleIndex,
                                    connectIndex,
                                    e.target.checked
                                  )
                                }
                                className="w-4 h-4"
                                title={isFrozen ? "Nominations are frozen" : ""}
                              />
                              {/* {userNominated && (
                                <span className="text-xs text-green-600 font-semibold">Nominated</span>
                              )} */}
                            </>
                          );
                        })()}
                      </div>
                    </td>
                    {connectIndex === 0 && (
                      <>
                        <td rowSpan={mod.connects.length} className="px-4 py-3 border-r border-gray-200 text-sm align-top">
                          {mod.assessmentDate}
                        </td>
                        <td rowSpan={mod.connects.length} className="px-4 py-3 text-sm align-top">
                          {mod.quizDate}
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
              <tr><td colSpan="9"><center>
                <button
                  onClick={() => handleSubmitNomination(program)}
                  disabled={(() => {
                    const { newNominations } = getNominationStatus(program);
                    // Enable only if there are new nominations to save and not frozen
                    return newNominations === 0 || isFrozen;
                  })()}
                  className={`px-4 py-2 text-sm rounded-md focus:outline-none focus:ring-2 ${
                    (() => {
                      const { newNominations } = getNominationStatus(program);
                      return newNominations === 0 || isFrozen
                        ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500';
                    })()
                  }`}
                  title={isFrozen ? "Cannot nominate when nominations are frozen" : ""}
                >
                  Save Nomination
                </button>
              </center>
              </td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Blended Learning Nomination Page</h2>
      <div className="flex flex-col items-center justify-center mb-4">
        <label className="block text-gray-700" htmlFor="month">Select Month</label>
        <input
          id="month"
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="w-60 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          required
        />
        {user && !user.track && (
          <div className="mt-2 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-md">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">Warning: Your user profile does not have a track assigned. You may not see any programs available for nomination.</span>
            </div>
          </div>
        )}
      </div>

      {selectedMonth && !loadingExistingOfferings && (
        <div className="bg-white rounded-lg shadow-md border border-gray-300 overflow-hidden mb-8">
          <div className="bg-blue-500 text-white px-6 py-4 flex justify-between items-center">
            <h3 className="text-lg font-semibold">
              Programs Open for Nomination - {user?.track || 'Your Track'} ({selectedMonth ? new Date(selectedMonth).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'Selected Month'})
            </h3>
          </div>

          {(existingOfferings.length === 0) && (
            <div className="px-6 py-8 text-center bg-gray-50">
              <div className="text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <p className="text-lg font-medium text-gray-900 mb-2">No Programs Available</p>
                <p className="text-sm text-gray-600">
                  There are currently no blended learning programs open for nomination for your track ({user?.track || 'Unknown Track'}) in {new Date(selectedMonth).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}.
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Please check with your administrator or try a different month.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {loadingExistingOfferings && (
        <div className="bg-white rounded-lg shadow-md border border-gray-300 overflow-hidden mb-8">
          <div className="bg-blue-500 text-white px-6 py-4 flex justify-between items-center">
            <h3 className="text-lg font-semibold">
              Programs Open for Nomination - {user?.track || 'Your Track'} ({selectedMonth ? new Date(selectedMonth).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'Selected Month'})
            </h3>
          </div>
          <div className="px-6 py-4 bg-gray-50 border-b">
            <div className="flex items-center space-x-2 text-gray-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
              <span className="text-sm">Loading programs open for nomination for your track...</span>
            </div>
          </div>
        </div>
      )}

      {(existingOfferings.length > 0) && !loadingExistingOfferings && (
        <div className="bg-white rounded-lg shadow-md border border-gray-300 overflow-hidden mb-8">

          <div className="divide-y divide-gray-200">
            {existingOfferings.map((program) => (
              <div key={program.id} className="bg-white">
                <div className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => toggleProgramExpansion(program.id)}
                      className="p-1 hover:bg-gray-200 rounded transition-colors"
                    >
                      <svg
                        className={`w-5 h-5 transform transition-transform ${expandedPrograms[program.id] ? "rotate-90" : ""
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    {/* Hari code to remove the existing and edit, save, remove buttons */}
                    <div>
                      <h4 className="text-lg font-medium text-gray-800 flex items-center space-x-2">
                        <span>{program.blendedProgramName}</span>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Instance #{program.blendedProgramId || program.id.split('-')[1] || 'N/A'}</span>
                        {program.status === 'FINALIZED' && <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded">✓ FINALIZED</span>}
                        {program.isEditing && <span className="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Editing</span>}
                      </h4>
                      <p className="text-sm text-gray-600">
                        Code: {program.programWDCourseCode || 'N/A'} | {program.modules.length} modules | ID: {program.blendedProgramId || 'N/A'}
                      </p>
                    </div>
                  </div>

                </div>
                {expandedPrograms[program.id] && renderProgramTable(program)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default SMEBlendedLearningNomination;